'use client'

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons'
import {
  Button,
  ConfigProvider,
  Flex,
  Layout as AntdLayout,
  Menu,
} from 'antd'
import Image from 'next/image'
import React, {
  createElement,
  useCallback,
} from 'react'
import { useToggle } from 'react-use'

import LogoText from '@/assets/logo-text.png'
import { FeatureKey } from '@/constants/feature-key'
import { colorPrimary } from '@/constants/theme'
import { setFeatureKeys } from '@/redux/global'
import { selectMenuItems } from '@/redux/menu-selector'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import LayoutHeader from './layout-header'
import styles from './layout.module.css'

const { Content, Sider } = AntdLayout

const selector = createAppSelector([
  ({ global }) => global.featureKeys,
  selectMenuItems,
], (featureKeys, menuItems) => ({
  featureKeys,
  menuItems,
}))

export default function Layout() {
  const { featureKeys, menuItems } = useAppSelector(selector)
  const page = window.menuConfig[featureKeys.at(-1)!]?.page
  const dispatch = useAppDispatch()

  const [collapsed, toggleCollapsed] = useToggle(false)

  const onMenuChange = useCallback(
    ({ keyPath }: { keyPath: string[] }) => {
      if (keyPath.includes(FeatureKey.Messages)) {
        window.open('/message', '_blank')
        return
      }
      dispatch(setFeatureKeys(keyPath.slice().reverse() as FeatureKey[]))
    }, [dispatch],
  )

  function handleOpenMenu(featureKeys: FeatureKey[]) {
    dispatch(setFeatureKeys(featureKeys))
    toggleCollapsed(true)
  }

  return (
    <AntdLayout className={styles.mainLayout}>
      <LayoutHeader onHeaderMenuClick={handleOpenMenu} />
      <AntdLayout className={styles.contentLayout}>
        <ConfigProvider
          theme={{
            components: {
              Layout: {
                siderBg: colorPrimary,
              },
              Menu: {
                itemColor: 'white',
                itemHoverColor: 'white',
                itemHoverBg: 'rgba(255,255,255,0.1)',
                itemSelectedColor: 'white',
                itemSelectedBg: '#333333',
                subMenuItemSelectedColor: 'white',
                popupBg: colorPrimary,
                itemBg: colorPrimary,
              },
            },
          }}
        >
          <Sider
            className={styles.mainSider}
            width={200}
            collapsed={collapsed}
            collapsedWidth={56}
          >
            <Flex
              className={styles.mainSider}
              vertical={collapsed}
              align="center"
              justify="space-between"
            >
              {!collapsed && <Image className={styles.logo} src={LogoText} alt="Logo" width={130} />}
              <Button
                className={styles.toggleBtn}
                type="text"
                onClick={toggleCollapsed}
              >
                {collapsed ? (
                  <MenuUnfoldOutlined style={{ color: 'white' }} />
                ) : (
                  <MenuFoldOutlined style={{ color: 'white' }} />
                )}
              </Button>
            </Flex>
            <Menu
              mode="inline"
              selectedKeys={featureKeys}
              defaultOpenKeys={featureKeys}
              items={menuItems}
              onSelect={onMenuChange}
            />
          </Sider>
        </ConfigProvider>
        <Content className={styles.pageContent}>
          {page && createElement(page)}
        </Content>
      </AntdLayout>
    </AntdLayout>
  )
}
